import { z } from "zod";
import type { Api } from "../../services/api";
import { liveActiveTurn } from "../../../../api/src/shared/domain/gpt-turn";

/**
 * Render helpers over the parts[] transcript the conversation endpoints
 * return (populated shape: internal sources hydrated to articles, PubMed
 * sources as snapshots — the agent runtime renders both, distinguished by a
 * provenance badge).
 */
type Conversation =
  Api["get"]["/webapp/private/conversations/:id"]["output"]["body"];
export type ConversationMessage = Conversation["messages"][number];
type Part = ConversationMessage["parts"][number];
type SourcePart = Extract<Part, { type: "source" }>;
// Both the standalone `source` part and the agentic `tool` part carry the same
// populated source union (internal hydrated to an article, PubMed a snapshot).
type PopulatedSource = SourcePart["source"];
// What a snapshot says the paper IS, methodologically: the canonical article
// type, and the finer design under the observational bucket when the sweep job
// recovered one. Read off the endpoint's own contract rather than restated, so
// the row can only ever render a vocabulary the API actually serves.
type SnapshotStudyDesign = Extract<
  PopulatedSource,
  { provenance: "pubmed" }
>["studyDesign"];

/**
 * Minimal source reference the GPT source components render from. A
 * discriminated union over provenance:
 * - `juisci_internal`: an article reference (`id` batch-fetches the details,
 *   `slug` links once resolved). Streamed/tool refs carry no slug until the
 *   batch fetch returns.
 * - `pubmed`: an external snapshot (no local article) — rendered from the
 *   captured metadata, linking out to PubMed.
 * - `web`: an open-web snapshot (Exa `searchScientificWeb`) — rendered from
 *   the captured metadata, linking out to the page.
 *
 * Both the populated DB transcript and the streamed `data-sources` / tool parts
 * reduce to this shape.
 */
export type GptSourceRef =
  | {
      provenance: "juisci_internal";
      id: string;
      title: string | null;
      slug: string | null;
    }
  | {
      provenance: "pubmed";
      externalId: string;
      title: string;
      url: string | null;
      authors: string[];
      year: number | null;
      journal: string | null;
      studyDesign: SnapshotStudyDesign;
    }
  | {
      provenance: "web";
      url: string;
      title: string;
      publisher: string;
      year: number | null;
      // First author first, same shape as the pubmed variant so one formatter
      // serves both. `publisher` (the hostname) is the SUBSTITUTE identity.
      authors: string[];
      // Resolved after the turn from the DOI in the URL; null while unresolved,
      // and for a page that is not an article at all.
      journal: string | null;
      studyDesign: SnapshotStudyDesign;
    }
  | {
      provenance: "uploaded_document";
      documentId: string;
      filename: string;
    };

// Structural over the part union: works on both the raw transcript (history
// list — source parts are references) and the populated one (conversation
// detail — source parts are hydrated articles).
export function messageText(message: {
  parts: ReadonlyArray<
    | { type: "text"; text: string }
    | { type: "source" }
    | { type: "tool" }
    | { type: "topicSuggestion" }
    | { type: "roomSuggestion" }
  >;
}): string {
  return message.parts
    .flatMap((part) => (part.type === "text" ? [part.text] : []))
    .join("\n\n");
}

// The label of a conversation in a list. A conversation is titled once its
// first turn has landed; before that its opening question is the only label it
// has, and while that first turn is still being WRITTEN (lists show such rows
// so a user who backgrounded the app finds their way back to it), the pending
// question is.
export function conversationListTitle(
  conversation: Pick<
    Api["get"]["/webapp/private/conversations"]["output"]["body"]["docs"][number],
    "title" | "messages" | "activeTurnStartedAt" | "activeTurnQuestion"
  >,
  nowMs: number
): string {
  if (conversation.title !== null) return conversation.title;
  const firstMessage = conversation.messages.at(0);
  if (firstMessage !== undefined) return messageText(firstMessage);
  const activeTurn = liveActiveTurn(conversation, nowMs);
  return activeTurn === null ? "" : activeTurn.question;
}

// The assistant engine titles a conversation after its first run.
export function assistantConversationListTitle(
  conversation: Pick<
    Api["get"]["/assistant/conversations"]["output"]["body"]["docs"][number],
    "title" | "question"
  >
): string {
  if (conversation.title !== null) return conversation.title;
  return conversation.question === null ? "" : conversation.question;
}

// Title fallback for conversation lists: the text of the first message.
export function firstMessageText(
  messages: ReadonlyArray<Parameters<typeof messageText>[0]>
): string {
  const first = messages.at(0);
  return first === undefined ? "" : messageText(first);
}

function refFromSource(source: PopulatedSource): GptSourceRef {
  if (source.provenance === "juisci_internal")
    return {
      provenance: "juisci_internal",
      id: source.article.id,
      title: source.article.title,
      slug: source.article.slug,
    };
  if (source.provenance === "uploaded_document")
    return {
      provenance: "uploaded_document",
      documentId: source.documentId,
      filename: source.filename,
    };
  if (source.provenance === "web")
    return {
      provenance: "web",
      url: source.url,
      title: source.title,
      publisher: source.publisher,
      year: source.year,
      authors: source.authors,
      journal: source.journal,
      studyDesign: source.studyDesign,
    };
  return {
    provenance: "pubmed",
    externalId: source.externalId,
    title: source.title,
    url: source.url,
    authors: source.authors,
    year: source.year,
    journal: source.journal,
    studyDesign: source.studyDesign,
  };
}

function refKey(ref: GptSourceRef): string {
  switch (ref.provenance) {
    case "juisci_internal":
      return `internal:${ref.id}`;
    case "pubmed":
      return `pubmed:${ref.externalId}`;
    case "web":
      return `web:${ref.url}`;
    case "uploaded_document":
      return `uploaded:${ref.documentId}`;
    default:
      throw new Error(ref satisfies never);
  }
}

// The deterministic pre-fetch and an agentic re-query can surface the same
// article; the up-front internal sources and a tool's internal sources overlap.
// Dedup by provenance-scoped identity, keeping first appearance (retrieval
// order).
export function dedupeSourceRefs(refs: GptSourceRef[]): GptSourceRef[] {
  const seen = new Set<string>();
  return refs.flatMap((ref) => {
    const key = refKey(ref);
    if (seen.has(key)) return [];
    seen.add(key);
    return [ref];
  });
}

// ── The turn's proposal ─────────────────────────────────────────────────────

// One curated topic the agent proposed adding to the user's saved interests.
export const suggestedTopicSchema = z.object({
  uid: z.string(),
  name: z.string(),
});
export type SuggestedTopic = z.infer<typeof suggestedTopicSchema>;

// One Room the agent proposed opening. Fully resolved server-side (localized
// name, organisation, avatar) because the card is drawn from the STREAM — the
// client has no room row to read at that point.
export const suggestedRoomSchema = z.object({
  id: z.string(),
  name: z.string(),
  shortDescription: z.string().nullable(),
  organisationName: z.string(),
  imageUrl: z.string().nullable(),
  // Already a member? The card changes register — a return, not a discovery
  // (AC-B4b) — and the two are reported as separate outcomes: acquisition
  // (`room_joined`) versus activation (`room_opened`).
  isMember: z.boolean(),
  // Which path produced this room: the sources the answer CITED, or the subject
  // the model named. Reported on every card event so the weaker trigger can be
  // killed on data — hardcoding it would make the evidence trigger look like it
  // never fires.
  trigger: z.enum(["evidence", "topic_match"]),
  // How many cited sources came from this room. Always 0 under `topic_match`
  // (nothing was cited on that path), so segment on `trigger` before reading it.
  citedSourceCount: z.number(),
});
export type SuggestedRoom = z.infer<typeof suggestedRoomSchema>;

// What a finished turn proposes, if anything — mirrors the server's
// `GptTurnSuggestion` (the stream wire is hand-declared on both sides, like
// `GptSourceRef`: data parts don't ride the OpenAPI contract). A union rather
// than two lists because the composer band holds exactly ONE widget. The schema
// doubles as the boundary validator when the proposal rides the new-chat →
// conversation hand-off through router location state (see `GPTConversationPage`).
export const gptTurnSuggestionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }),
  z.object({
    kind: z.literal("topics"),
    topics: z.array(suggestedTopicSchema),
  }),
  z.object({ kind: z.literal("rooms"), rooms: z.array(suggestedRoomSchema) }),
]);
export type GptTurnSuggestion = z.infer<typeof gptTurnSuggestionSchema>;

// The persisted `topicSuggestion` / `roomSuggestion` parts are deliberately NOT
// read back here: a proposal is live-only (see `toUIMessage` — leaving the
// conversation dismisses it), so the stored parts are an archival record, like
// the tool parts.

// Reloaded-from-DB message → renderable refs. Sources are the standalone
// `source` parts only — the answer's CITED grounding (resolved server-side at
// finish). `tool` parts are the archival search trace and never surface as
// sources (uncited retrieval must not appear next to the answer).
export function messageSourceRefs(
  message: ConversationMessage
): GptSourceRef[] {
  return dedupeSourceRefs(
    message.parts.flatMap((part) => {
      switch (part.type) {
        case "text":
          return [];
        case "source":
          return [refFromSource(part.source)];
        case "tool":
        case "topicSuggestion":
        case "roomSuggestion":
          return [];
        default:
          throw new Error(part satisfies never);
      }
    })
  );
}

// ── The turn's action trace ─────────────────────────────────────────────────

/**
 * One thing the agent DID before it answered, as the chat prints it. The
 * transcript's `tool` parts are the record — they are written on every turn and
 * were, until now, archival only: the surface showed the step in flight and
 * then forgot it, so a reloaded conversation could not say what was searched.
 *
 * `kind` is the rendering family, not the tool: four search tools all read the
 * Juisci corpus and all print the same line. `query` is what the agent asked
 * for, null on the tools that take no query (a page or line range, a grep
 * pattern) and on transcripts written before the query crossed the wire.
 */
export type GptAction = {
  kind: GptActionKind;
  query: string | null;
};

export type GptActionKind =
  // The juisci search of one assistant mission, over the Juisci library, PubMed and the web.
  | "search"
  | "library"
  | "pubmed"
  | "web"
  // The assistant engine's `publication similar` and `publication get`.
  | "similar"
  | "read"
  | "files"
  | "fileRead"
  | "suggestTopics"
  | "suggestRooms"
  // The assistant engine's reading of the question, and each assumption it proceeds on.
  | "question"
  | "assumption"
  // Each question the assistant engine asked the reader to clarify theirs, once answered.
  | "clarification";

export type GptToolName = Extract<
  ConversationMessage["parts"][number],
  { type: "tool" }
>["toolName"];

// `satisfies Record<GptToolName, …>` is the point of this table: a tool added
// to the transcript's `toolName` enum server-side fails to compile here until
// it declares how the trace prints it, rather than silently vanishing from the
// record of what the agent did.
const ACTION_KIND_BY_TOOL = {
  searchInternalCorpus: "library",
  // The Search-mode tool reads the same Juisci publications the corpus tool
  // does, and the referenced-source tools read a whitelisted slice of them.
  // One line covers all four: the reader cares which CORPUS was searched.
  searchPublications: "library",
  searchReferencedSources: "library",
  searchReferencedPlaylist: "library",
  searchPubMed: "pubmed",
  searchScientificWeb: "web",
  searchUploadedFiles: "files",
  readDocumentPages: "fileRead",
  readFileLines: "fileRead",
  grepFile: "fileRead",
  suggestTopicInterests: "suggestTopics",
  suggestRooms: "suggestRooms",
} as const satisfies Record<
  GptToolName,
  Exclude<GptActionKind, "question" | "assumption" | "clarification">
>;

// Keyed lookup for the LIVE path, where the AI SDK hands back a tool name as a
// bare `string` (its tool parts are dynamic) and no narrowing is possible. A
// name absent from the table is a tool the trace does not print — the decline
// signal, or a call from a build newer than this client.
const actionKindByToolName = new Map<string, GptActionKind>(
  Object.entries(ACTION_KIND_BY_TOOL)
);

export function gptActionFromTool(
  toolName: string,
  query: string | null
): GptAction | null {
  const kind = actionKindByToolName.get(toolName);
  return kind === undefined ? null : { kind, query };
}

// Retrieval calls only — what the header counts as "n searches". The two
// suggestion tools are traced (the agent did run them) but proposing a Room is
// not a search, and counting it as one would inflate the number the reader uses
// to judge how hard the answer was worked for. The question and clarification entries are not searches either.
export function gptSearchCount(actions: ReadonlyArray<GptAction>): number {
  return actions.filter(
    (action) =>
      action.kind !== "suggestTopics" &&
      action.kind !== "suggestRooms" &&
      action.kind !== "question" &&
      action.kind !== "assumption" &&
      action.kind !== "clarification"
  ).length;
}

// Reloaded-from-DB message → its action trace, in call order.
export function messageActions(message: ConversationMessage): GptAction[] {
  return message.parts.flatMap((part) => {
    switch (part.type) {
      case "text":
      case "source":
      case "topicSuggestion":
      case "roomSuggestion":
        return [];
      case "tool": {
        const action = gptActionFromTool(part.toolName, part.query);
        return action === null ? [] : [action];
      }
      default:
        throw new Error(part satisfies never);
    }
  });
}

/**
 * Wall-clock seconds a persisted turn took: the gap between the question and
 * the answer that was written for it. The transcript stamps every message, so
 * unlike the live `useStreamDuration` this survives a reload — which is the
 * whole point of the trace.
 *
 * Null when the stamps do not parse, never a made-up 0. Pairing an answer with
 * its question is the caller's job: an answer with nothing before it has no
 * duration to compute, and that is a shape this function never sees.
 */
export function turnSeconds(
  question: ConversationMessage,
  answer: ConversationMessage
): number | null {
  const askedAt = Date.parse(question.createdAt);
  const answeredAt = Date.parse(answer.createdAt);
  if (Number.isNaN(askedAt) || Number.isNaN(answeredAt)) return null;
  return Math.max(1, Math.round((answeredAt - askedAt) / 1000));
}
